import React, { useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../state/AuthContext";
import SpeechInput from "../components/SpeechInput";

const initialForm = {
  fullName: "",
  address: "",
  mobile: "",
  workTypeId: "",
  workId: "",
  paymentAmount: "",
  paidAmount: "",
  paymentMode: "CASH",
  referenceNo: "",
  paymentNotes: "",
  vehicleNo: "",
  licenseType: "",
  previousDocumentNo: "",
  birthDate: "",
  email: "",
  aadharNo: "",
  panNo: "",
};

export default function PublicEnquiryPage({
  apiPath = "/api/public/enquiries",
  title = "Customer Enquiry Form",
  description = "Fill customer details and submit. An official Application No will be generated.",
  submitLabel = "Submit Customer Form",
}) {
  const { http, user } = useContext(AuthContext);
  const isAdmin = user?.role === "admin";

  const [form, setForm] = useState(initialForm);
  const [workTypes, setWorkTypes] = useState([]);
  const [works, setWorks] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    async function loadOptions() {
      setLoadingOptions(true);
      try {
        const res = await http.get("/api/public/work-options");
        if (!alive) return;
        setWorkTypes(res.data?.workTypes || []);
        setWorks(res.data?.works || []);
      } catch (err) {
        if (!alive) return;
        setError(err?.response?.data?.message || "Failed to load work options.");
      } finally {
        if (alive) setLoadingOptions(false);
      }
    }

    loadOptions();
    return () => {
      alive = false;
    };
  }, [http]);

  const selectedWorkType = workTypes.find((item) => String(item.id) === String(form.workTypeId));
  const filteredWorks = works.filter(
    (item) => String(item.workTypeId) === String(form.workTypeId)
  );
  const selectedWork = works.find((item) => String(item.id) === String(form.workId));

  const totalFeeNumber = Number(form.paymentAmount || 0);
  const paidAmountNumber = Number(form.paidAmount || 0);
  const calculatedBalance = Math.max(0, totalFeeNumber - paidAmountNumber);

  const canSubmit = useMemo(() => {
    // 1) Base fields: Birthdate is NOT compulsory for any user
    const hasBaseFields =
      form.fullName.trim() &&
      form.mobile.trim() &&
      form.address.trim();

    if (!hasBaseFields) return false;

    // If work type is selected, work item must be selected
    if (form.workTypeId && !form.workId) {
      return false;
    }

    if (selectedWorkType?.code === "vehicle" && !form.vehicleNo.trim()) {
      return false;
    }

    if (selectedWorkType?.code === "driving_licence" && !form.licenseType.trim()) {
      return false;
    }

    if (isAdmin && form.paidAmount !== "") {
      const paid = Number(form.paidAmount);
      if (isNaN(paid) || paid < 0) return false;
    }

    return true;
  }, [form, selectedWorkType, isAdmin]);

  const onChange = (e) => {
    const { name, value } = e.target;
    const nextValue =
      name === "fullName" || name === "address"
        ? value.toUpperCase()
        : value;

    setForm((s) => {
      if (name === "workTypeId") {
        return {
          ...s,
          workTypeId: nextValue,
          workId: "",
          paymentAmount: "",
          paidAmount: "",
          vehicleNo: "",
          licenseType: "",
          previousDocumentNo: "",
        };
      }

      if (name === "workId") {
        const nextWork = works.find((item) => String(item.id) === String(nextValue));
        const feeVal = nextWork ? String(nextWork.fee) : "";
        return {
          ...s,
          workId: nextValue,
          paymentAmount: feeVal,
        };
      }

      return { ...s, [name]: nextValue };
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const res = await http.post(apiPath, form);
      setResult(res.data);
      setForm(initialForm);
    } catch (err) {
      setError(
        err?.response?.data?.message || "Failed to submit customer form. Try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="row justify-content-center">
      <div className="col-12 col-lg-9 col-xl-8">
        <div className="card shadow-sm border-0">
          <div className="card-body p-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 className="card-title mb-1 fw-bold text-primary">{title}</h5>
                <p className="text-muted small mb-0">{description}</p>
              </div>
              {isAdmin && (
                <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-2">
                  🛡️ Admin Mode
                </span>
              )}
            </div>

            {result ? (
              <div className="alert alert-success border-success-subtle shadow-sm mb-4">
                <div className="d-flex align-items-center gap-2">
                  <span className="fs-4">✅</span>
                  <div>
                    <div className="fw-bold fs-6">Customer Form Submitted Successfully!</div>
                    <div className="mt-1">
                      Application No: <span className="badge bg-dark fs-6 font-monospace">{result.applicationNo}</span>
                    </div>
                  </div>
                </div>

                {result.receiptNo ? (
                  <div className="mt-3 pt-2 border-top border-success-subtle row g-2 small">
                    <div className="col-12 col-sm-4">
                      Receipt No: <strong className="font-monospace">{result.receiptNo}</strong>
                    </div>
                    <div className="col-12 col-sm-4">
                      Amount Paid: <strong className="text-success">₹{Number(result.paidAmount || 0).toFixed(2)}</strong>
                    </div>
                    <div className="col-12 col-sm-4">
                      Balance Remaining:{" "}
                      <strong className={result.pendingBalance > 0 ? "text-danger" : "text-success"}>
                        ₹{Number(result.pendingBalance || 0).toFixed(2)}
                      </strong>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {error ? <div className="alert alert-danger">{error}</div> : null}
            {loadingOptions ? (
              <div className="alert alert-secondary py-2 small">Loading work and service options...</div>
            ) : null}

            <form onSubmit={onSubmit} className="row g-3">
              {/* 1. Full Name */}
              <div className="col-12">
                <label className="form-label fw-semibold">Customer Full Name *</label>
                <SpeechInput
                  as="input"
                  name="fullName"
                  value={form.fullName}
                  onChange={onChange}
                  inputClassName="text-uppercase"
                  placeholder="Enter customer full name"
                  required
                />
              </div>

              {/* 2. Address */}
              <div className="col-12">
                <label className="form-label fw-semibold">Address *</label>
                <SpeechInput
                  as="textarea"
                  name="address"
                  value={form.address}
                  onChange={onChange}
                  inputClassName="text-uppercase"
                  rows={2}
                  placeholder="Customer address"
                  required
                />
              </div>

              {/* 3. Mobile No */}
              <div className="col-12 col-md-6">
                <label className="form-label fw-semibold">Mobile No *</label>
                <SpeechInput
                  as="input"
                  name="mobile"
                  value={form.mobile}
                  onChange={onChange}
                  inputMode="numeric"
                  placeholder="10-digit mobile number"
                  required
                />
              </div>

              {/* --- DIRECTLY AFTER MOBILE NO: WORK TYPE, WORK, PAYMENT OPTIONS --- */}
              {/* 4. Work Type */}
              <div className="col-12 col-md-6">
                <label className="form-label fw-semibold">Work Type</label>
                <select
                  name="workTypeId"
                  value={form.workTypeId}
                  onChange={onChange}
                  className="form-select"
                >
                  <option value="">Select Work Type</option>
                  {workTypes.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* 5. Work */}
              <div className="col-12 col-md-6">
                <label className="form-label fw-semibold">Work</label>
                <select
                  name="workId"
                  value={form.workId}
                  onChange={onChange}
                  className="form-select"
                  disabled={!form.workTypeId}
                >
                  <option value="">{form.workTypeId ? "Select Work" : "-- Select Work Type First --"}</option>
                  {filteredWorks.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} {item.fee ? `(₹${Number(item.fee).toFixed(0)})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              {/* 6. Payment Options & Columns */}
              {isAdmin ? (
                /* ADMIN VIEW: Total Fee, Paid Amount Column, Dynamic Balance Amount, Payment Option */
                <>
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold">Total Fee / Payment (₹)</label>
                    <SpeechInput
                      as="input"
                      name="paymentAmount"
                      value={form.paymentAmount}
                      onChange={onChange}
                      inputMode="decimal"
                      placeholder="0.00"
                    />
                    {selectedWork ? (
                      <div className="form-text">
                        Standard Fee: ₹{Number(selectedWork.fee || 0).toFixed(2)}
                      </div>
                    ) : null}
                  </div>

                  {/* PAID AMOUNT COLUMN */}
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold text-success">
                      Paid Amount (₹)
                    </label>
                    <SpeechInput
                      as="input"
                      name="paidAmount"
                      value={form.paidAmount}
                      onChange={onChange}
                      inputMode="decimal"
                      placeholder="Enter paid amount (e.g. 500)"
                    />
                    {paidAmountNumber > totalFeeNumber && totalFeeNumber > 0 ? (
                      <div className="form-text text-danger">⚠️ Paid amount exceeds total fee.</div>
                    ) : null}
                  </div>

                  {/* BALANCE AMOUNT COLUMN */}
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold text-secondary">
                      Balance Amount (₹)
                    </label>
                    <div
                      className={`form-control d-flex align-items-center justify-content-between ${
                        calculatedBalance === 0 && totalFeeNumber > 0 && paidAmountNumber > 0
                          ? "bg-success-subtle text-success border-success fw-bold"
                          : calculatedBalance > 0
                          ? "bg-warning-subtle text-dark border-warning fw-bold"
                          : "bg-light text-muted"
                      }`}
                      style={{ minHeight: "38px" }}
                    >
                      <span className="fs-6">₹{calculatedBalance.toFixed(2)}</span>
                      <span
                        className={`badge ${
                          calculatedBalance === 0 && totalFeeNumber > 0 && paidAmountNumber > 0
                            ? "bg-success"
                            : calculatedBalance > 0
                            ? "bg-warning text-dark"
                            : "bg-secondary"
                        }`}
                      >
                        {calculatedBalance === 0 && totalFeeNumber > 0 && paidAmountNumber > 0
                          ? "✓ FULLY PAID"
                          : calculatedBalance > 0
                          ? "PENDING"
                          : "ZERO BALANCE"}
                      </span>
                    </div>
                  </div>

                  {/* PAYMENT OPTION / MODE */}
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold">Payment Option / Mode</label>
                    <select
                      className="form-select"
                      name="paymentMode"
                      value={form.paymentMode}
                      onChange={onChange}
                    >
                      <option value="CASH">💵 Cash</option>
                      <option value="UPI">📱 UPI / QR Code</option>
                      <option value="ONLINE">💳 Online / Card</option>
                      <option value="BANK_TRANSFER">🏦 Bank Transfer</option>
                      <option value="CHEQUE">📝 Cheque</option>
                    </select>
                  </div>

                  {paidAmountNumber > 0 && (
                    <>
                      <div className="col-12 col-md-6">
                        <label className="form-label small text-muted">Payment Reference / UTR / Cheque No</label>
                        <SpeechInput
                          as="input"
                          name="referenceNo"
                          value={form.referenceNo}
                          onChange={onChange}
                          placeholder="Optional transaction reference"
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label small text-muted">Payment Remarks / Notes</label>
                        <SpeechInput
                          as="input"
                          name="paymentNotes"
                          value={form.paymentNotes}
                          onChange={onChange}
                          placeholder="Optional note"
                        />
                      </div>
                    </>
                  )}
                </>
              ) : (
                /* REGULAR / PUBLIC USER VIEW: Payment Amount and Payment Option */
                <>
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold">Payment / Fee (₹)</label>
                    <SpeechInput
                      as="input"
                      name="paymentAmount"
                      value={form.paymentAmount}
                      onChange={onChange}
                      inputMode="decimal"
                      placeholder="Fee amount"
                    />
                    {selectedWork ? (
                      <div className="form-text">
                        Standard fee: ₹{Number(selectedWork.fee || 0).toFixed(2)}
                      </div>
                    ) : null}
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold">Payment Option</label>
                    <select
                      className="form-select"
                      name="paymentMode"
                      value={form.paymentMode}
                      onChange={onChange}
                    >
                      <option value="CASH">💵 Pay at Office / Cash</option>
                      <option value="UPI">📱 UPI / QR Code</option>
                      <option value="ONLINE">💳 Online / Card</option>
                      <option value="BANK_TRANSFER">🏦 Bank Transfer</option>
                      <option value="CHEQUE">📝 Cheque</option>
                    </select>
                  </div>
                </>
              )}

              {/* Conditional Vehicle details if vehicle work type */}
              {selectedWorkType?.code === "vehicle" ? (
                <div className="col-12 col-md-6">
                  <label className="form-label fw-semibold">Vehicle No *</label>
                  <SpeechInput
                    as="input"
                    name="vehicleNo"
                    value={form.vehicleNo}
                    onChange={onChange}
                    placeholder="e.g. MH12AB1234"
                    required
                  />
                </div>
              ) : null}

              {/* Conditional License details if driving licence work type */}
              {selectedWorkType?.code === "driving_licence" ? (
                <>
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold">License Type *</label>
                    <SpeechInput
                      as="input"
                      name="licenseType"
                      value={form.licenseType}
                      onChange={onChange}
                      placeholder="e.g. MCWG, LMV"
                      required
                    />
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label">Previous Document No</label>
                    <SpeechInput
                      as="input"
                      name="previousDocumentNo"
                      value={form.previousDocumentNo}
                      onChange={onChange}
                      placeholder="Optional previous license or doc no"
                    />
                  </div>
                </>
              ) : null}

              <div className="col-12 my-2">
                <hr className="text-muted my-1" />
                <span className="badge bg-light text-secondary border px-2 py-1 small">
                  Additional Customer Details (Optional)
                </span>
              </div>

              {/* 7. Birth Date - NOT COMPULSORY FOR ALL USERS */}
              <div className="col-12 col-md-6">
                <label className="form-label">
                  Birth Date <span className="text-muted fw-normal small">(Optional)</span>
                </label>
                <input
                  name="birthDate"
                  value={form.birthDate}
                  onChange={onChange}
                  className="form-control"
                  type="date"
                />
              </div>

              {/* 8. E-Mail Id */}
              <div className="col-12 col-md-6">
                <label className="form-label">
                  E-Mail Id <span className="text-muted fw-normal small">(Optional)</span>
                </label>
                <SpeechInput
                  as="input"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="name@example.com"
                />
              </div>

              {/* 9. Adhar Card No */}
              <div className="col-12 col-md-6">
                <label className="form-label">
                  Adhar Card No <span className="text-muted fw-normal small">(Optional)</span>
                </label>
                <SpeechInput
                  as="input"
                  name="aadharNo"
                  value={form.aadharNo}
                  onChange={onChange}
                  placeholder="12-digit Aadhar number"
                />
              </div>

              {/* 10. PAN No */}
              <div className="col-12 col-md-6">
                <label className="form-label">
                  PAN No <span className="text-muted fw-normal small">(Optional)</span>
                </label>
                <SpeechInput
                  as="input"
                  name="panNo"
                  value={form.panNo}
                  onChange={onChange}
                  placeholder="10-digit PAN number"
                />
              </div>

              {/* Submit Action */}
              <div className="col-12 d-flex justify-content-between align-items-center pt-3 border-top mt-4">
                <div className="text-muted small">
                  Fields marked with <span className="text-danger fw-bold">*</span> are required. Birth Date is optional.
                </div>
                <button
                  type="submit"
                  disabled={!canSubmit || submitting}
                  className="btn btn-primary px-4 py-2 fw-semibold shadow-sm"
                >
                  {submitting ? "Saving Customer..." : submitLabel}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

